import React, { useState } from "react";

const faqs = [
  {
    q: "Are your marine fish wild-caught or captive-bred?",
    a: "We offer both. Captive-bred fish are prioritized because they adapt better, eat readily, and show less stress. Wild-caught species come from responsible, sustainable suppliers in the Maldives."
  },
  {
    q: "Do the fish come with a health guarantee?",
    a: "Yes. All fish undergo a 7–14 day quarantine before sale. We guarantee healthy, eating, disease-free fish. Issues on arrival must be reported within 2 hours with photos or video."
  },
  {
    q: "Do you ship marine fish nationwide?",
    a: "Yes. We ship via overnight express using insulated boxes, oxygen-filled bags, and heat/cold packs. Someone must be present to receive the order for Live Arrival Guarantee."
  },
  {
    q: "What water parameters do your fish require?",
    a: "Most marine fish thrive at salinity 1.023–1.025 SG, temperature 24–26°C, pH 8.1–8.4, ammonia & nitrite 0 ppm, and nitrate below 20 ppm."
  },
  {
    q: "Can I keep different species together?",
    a: "Yes, but compatibility matters. Each product page includes temperament and safe tankmates. You can contact us for personalized stocking advice."
  },
  {
    q: "How should I acclimate my new fish?",
    a: "Use the drip acclimation method: float the bag for 15 minutes, drip tank water for 30–45 minutes, and net the fish into your tank. Never add bag water to your aquarium."
  },
  {
    q: "Do I need a quarantine tank at home?",
    a: "A 40–60L quarantine tank is highly recommended. It prevents disease from entering your display tank and significantly increases survival rates."
  },
  {
    q: "What do your fish eat?",
    a: "Most species accept pellets, frozen mysis/brine shrimp, marine blends, and seaweed for herbivores. Each listing includes specific feeding guidance."
  },
  {
    q: "Can I return or exchange fish?",
    a: "For biosecurity reasons, we cannot accept returns or exchanges of live fish. Any arrival issues are covered by our Live Arrival Guarantee."
  },
  {
    q: "Do you offer beginner-friendly fish?",
    a: "Yes. Recommended beginner species include clownfish, firefish, gobies, blennies, royal grammas, and chromis."
  },
  {
    q: "How do I know which fish fit my tank size?",
    a: "All listings include minimum tank size and difficulty level. You can also contact us for free personalized recommendations."
  },
  {
    q: "Do your fish receive treatment before sale?",
    a: "Yes. All fish are parasite-screened, dewormed if needed, and monitored for behavior and feeding response during quarantine."
  }
];

const FAQ = () => {
  const [openIndex, setOpenIndex] = useState(null);

  const toggleFAQ = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <div style={{ background: 'var(--background)', minHeight: '100vh', paddingTop: '80px' }}>
      <div className="container" style={{ paddingBottom: '80px' }}>
        
        {/* Hero Section */}
        <div style={{ textAlign: 'center', marginBottom: '4rem' }}>
          <div style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '0.5rem',
            padding: '0.5rem 1rem',
            borderRadius: '50px',
            background: 'hsl(200, 85%, 25%, 0.1)',
            color: 'var(--primary)',
            marginBottom: '1rem'
          }}>
            <span style={{ fontSize: '1.5rem' }}>❓</span>
            <span style={{ fontSize: '0.875rem', fontWeight: '500' }}>FAQ</span>
          </div>
          <h1 className="font-display" style={{
            fontSize: 'clamp(3rem, 8vw, 5rem)',
            fontWeight: 'bold',
            color: 'var(--foreground)',
            marginBottom: '1rem'
          }}>
            Frequently Asked Questions
          </h1>
          <p style={{
            color: 'var(--muted-foreground)',
            maxWidth: '42rem',
            margin: '0 auto',
            fontSize: '1.25rem',
            lineHeight: '1.6'
          }}>
            Find answers to common questions about our marine fish, shipping, care requirements, and more.
          </p>
        </div>

        {/* FAQ Content */}
        <div style={{ maxWidth: '48rem', margin: '0 auto' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            {faqs.map((faq, i) => (
              <div
                key={i}
                className="reef-card"
                style={{
                  padding: '1.5rem',
                  transition: 'all 0.3s ease',
                  cursor: 'pointer'
                }}
                onMouseEnter={(e) => {
                  if (openIndex !== i) {
                    e.currentTarget.style.background = 'var(--muted)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (openIndex !== i) {
                    e.currentTarget.style.background = 'var(--card)';
                  }
                }}
              >
                <button
                  onClick={() => toggleFAQ(i)}
                  style={{
                    width: '100%',
                    textAlign: 'left',
                    display: 'flex',
                    alignItems: 'flex-start',
                    justifyContent: 'space-between',
                    gap: '1rem',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: 0
                  }}
                >
                  <span style={{
                    fontSize: '1.125rem',
                    fontWeight: '600',
                    color: 'var(--foreground)',
                    lineHeight: '1.5'
                  }}>
                    {faq.q}
                  </span>
                  <span style={{
                    color: 'var(--ocean-light)',
                    fontSize: '1.5rem',
                    fontWeight: 'bold',
                    minWidth: '2rem',
                    textAlign: 'center',
                    transition: 'transform 0.3s ease'
                  }}>
                    {openIndex === i ? "−" : "+"}
                  </span>
                </button>

                {openIndex === i && (
                  <div 
                    className="animate-fade-in"
                    style={{
                      marginTop: '1rem',
                      paddingTop: '1rem',
                      borderTop: '1px solid var(--border)'
                    }}
                  >
                    <p style={{
                      color: 'var(--muted-foreground)',
                      lineHeight: '1.7',
                      margin: 0,
                      fontSize: '1rem'
                    }}>
                      {faq.a}
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Contact CTA */}
        <div style={{
          textAlign: 'center',
          padding: '3rem 2rem',
          background: 'var(--gradient-ocean)',
          borderRadius: 'var(--radius)',
          color: 'white',
          marginTop: '4rem'
        }}>
          <h3 className="font-display" style={{
            fontSize: '1.75rem',
            fontWeight: '600',
            marginBottom: '1rem'
          }}>
            Still Have Questions?
          </h3>
          <p style={{
            fontSize: '1.125rem',
            marginBottom: '2rem',
            opacity: 0.9,
            maxWidth: '32rem',
            margin: '0 auto 2rem auto'
          }}>
            Our marine fish experts are here to help with personalized advice for your aquarium setup and fish selection.
          </p>
          <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
            <a href="/contact">
              <button className="btn-hero" style={{
                background: 'rgba(255, 255, 255, 0.2)',
                border: '1px solid rgba(255, 255, 255, 0.3)',
                backdropFilter: 'blur(4px)'
              }}>
                💬 Contact Our Experts
              </button>
            </a>
            <a href="https://wa.me/9609977542" target="_blank" rel="noopener noreferrer">
              <button className="btn-ghost" style={{
                color: 'white',
                borderColor: 'rgba(255, 255, 255, 0.3)'
              }}
              onMouseEnter={(e) => {
                e.target.style.background = 'rgba(255, 255, 255, 0.1)';
                e.target.style.color = 'white';
              }}
              onMouseLeave={(e) => {
                e.target.style.background = 'transparent';
                e.target.style.color = 'white';
              }}
              >
                📱 WhatsApp Us
              </button>
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FAQ;